/**
 * App Layout Component
 *
 * Main application layout with sidebar and content area.
 */

import { type ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/utils';
import { Logger, LogCategory, logNavigation } from '@/utils/logger';
import { useAuthStore, useSidebarCollapsed } from '@/stores';
import { ROUTES } from '@/constants';
import { Sidebar } from './Sidebar';
import { ToastContainer } from '@/components/ui/Toast';

// ============================================================================
// Types
// ============================================================================

interface AppLayoutProps {
  children: ReactNode;
}

// ============================================================================
// Component
// ============================================================================

export function AppLayout({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const isCollapsed = useSidebarCollapsed();
  const { isAuthenticated, checkSession, refreshSession } = useAuthStore();

  // Check authentication on mount and route changes
  useEffect(() => {
    Logger.debug(LogCategory.AUTH, 'AppLayout checking authentication', 'AppLayout', {
      isAuthenticated,
      path: location.pathname,
    });

    if (!isAuthenticated) {
      Logger.info(LogCategory.AUTH, 'User not authenticated, redirecting to login', 'AppLayout');
      logNavigation(location.pathname, ROUTES.LOGIN, 'redirect', { reason: 'not_authenticated' });
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }

    // Check if session is still valid
    const isValid = checkSession();
    if (!isValid) {
      Logger.info(LogCategory.AUTH, 'Session invalid, redirecting to login', 'AppLayout');
      logNavigation(location.pathname, ROUTES.LOGIN, 'redirect', { reason: 'session_expired' });
      navigate(ROUTES.LOGIN, { replace: true });
      return;
    }

    // Refresh session activity
    refreshSession();
  }, [isAuthenticated, location.pathname, navigate, checkSession, refreshSession]);

  // Track page views
  useEffect(() => {
    Logger.info(LogCategory.NAVIGATION, `Page view: ${location.pathname}`, 'AppLayout', {
      path: location.pathname,
      search: location.search,
    });
  }, [location]);

  // Refresh session on user activity
  useEffect(() => {
    const handleActivity = () => {
      refreshSession();
    };

    // Debounced activity tracking
    let timeout: ReturnType<typeof setTimeout>;
    const debouncedActivity = () => {
      clearTimeout(timeout);
      timeout = setTimeout(handleActivity, 1000);
    };

    window.addEventListener('click', debouncedActivity);
    window.addEventListener('keypress', debouncedActivity);
    window.addEventListener('scroll', debouncedActivity);

    return () => {
      clearTimeout(timeout);
      window.removeEventListener('click', debouncedActivity);
      window.removeEventListener('keypress', debouncedActivity);
      window.removeEventListener('scroll', debouncedActivity);
    };
  }, [refreshSession]);

  if (!isAuthenticated) {
    // Don't render layout while redirecting
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <main
        className={cn(
          'min-h-screen transition-all duration-300',
          isCollapsed ? 'ml-16' : 'ml-64'
        )}
      >
        {/* Page Content */}
        <div className="p-6">
          {children}
        </div>
      </main>

      {/* Toast Notifications */}
      <ToastContainer />
    </div>
  );
}

// ============================================================================
// Page Header Component
// ============================================================================

interface PageHeaderProps {
  title: string;
  description?: string;
  actions?: ReactNode;
  breadcrumbs?: Array<{ label: string; path?: string }>;
}

export function PageHeader({ title, description, actions, breadcrumbs }: PageHeaderProps) {
  const navigate = useNavigate();

  return (
    <div className="mb-6">
      {/* Breadcrumbs */}
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav className="mb-2" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm">
            {breadcrumbs.map((crumb, index) => (
              <li key={index} className="flex items-center gap-2">
                {index > 0 && <span className="text-slate-300">/</span>}
                {crumb.path ? (
                  <button
                    onClick={() => navigate(crumb.path!)}
                    className="text-slate-500 hover:text-slate-700"
                  >
                    {crumb.label}
                  </button>
                ) : (
                  <span className="text-slate-900 font-medium">{crumb.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{title}</h1>
          {description && (
            <p className="mt-1 text-slate-600">{description}</p>
          )}
        </div>
        {actions && <div className="flex items-center gap-3 shrink-0">{actions}</div>}
      </div>
    </div>
  );
}
